import { createRoot } from 'react-dom/client';
import { ClerkProvider } from "@clerk/clerk-react";
import { frFR } from '@clerk/localizations';
import App from './App.jsx';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import $ from 'jquery';
import { StrictMode } from 'react';
// Import de la configuration i18n
import './i18n/config';

// Configuration jQuery globale
window.$ = window.jQuery = $;
// Configuration QueryClient
const queryClient = new QueryClient();

// Clé publique Clerk
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error("Clerk publishable key not found. Check your environment variables.");
}

// Configuration de l'apparence Clerk
const clerkAppearance = {
  // Thème de base
  baseTheme: undefined,
  
  // Variables CSS globales pour personnaliser Clerk
  variables: {
    colorPrimary: '#182848',           // Indigo principal
    colorDanger: '#EF4444',            // Rouge pour les erreurs
    colorSuccess: '#10B981',           // Vert pour les succès
    colorWarning: '#F59E0B',           // Orange pour les avertissements
    colorTextOnPrimaryBackground: '#FFFFFF',
    colorTextSecondary: '#6B7280',
    colorBackground: '#FFFFFF',
    colorInputBackground: '#F9FAFB',
    colorInputText: '#111827',
    
    // Typographie
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyButtons: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '1rem',
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    // Espacements et formes
    borderRadius: '0.75rem',
    spacingUnit: '1rem',
  },
  
  // Personnalisation des éléments spécifiques
  elements: {
    // Modal de connexion
    modalContent: {
      borderRadius: '1rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    modalCloseButton: {
      color: '#6B7280',
      '&:hover': {
        color: '#111827',
      }
    },
    
    // Carte principale
    card: {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      borderRadius: '1rem',
      padding: '2rem',
      border: '1px solid #E5E7EB',
    },
    
    // En-têtes
    headerTitle: {
      color: '#111827',
      fontSize: '1.5rem',
      fontWeight: '700',
      marginBottom: '0.5rem',
    },
    headerSubtitle: {
      color: '#6B7280',
      fontSize: '0.95rem',
      fontWeight: '400',
    },
    
    // Boutons sociaux (Google, etc.)
    socialButtonsBlockButton: {
      backgroundColor: '#F9FAFB',
      border: '1.5px solid #E5E7EB',
      color: '#111827',
      fontWeight: '500',
      borderRadius: '0.75rem',
      padding: '0.75rem 1rem',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#FFFFFF',
        borderColor: '#4F46E5',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)',
      },
    },
    socialButtonsBlockButtonText: {
      fontWeight: '500',
      fontSize: '0.95rem',
    },
    
    // Bouton principal (Se connecter, S'inscrire)
    formButtonPrimary: {
      backgroundColor: '#182848',
      color: '#FFFFFF',
      fontWeight: '600',
      fontSize: '1rem',
      borderRadius: '0.75rem',
      padding: '0.875rem 1.5rem',
      transition: 'all 0.2s ease',
      border: 'none',
      boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)',
      '&:hover': {
        backgroundColor: '#4b6cb7',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(79, 70, 229, 0.5)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&:disabled': {
        backgroundColor: '#9CA3AF',
        cursor: 'not-allowed',
        transform: 'none',
      }
    },
    
    // Champs de saisie
    formFieldInput: {
      backgroundColor: '#F9FAFB',
      border: '1.5px solid #E5E7EB',
      borderRadius: '0.625rem',
      padding: '0.75rem 1rem',
      fontSize: '1rem',
      color: '#111827',
      transition: 'all 0.2s ease',
      '&:focus': {
        backgroundColor: '#FFFFFF',
        borderColor: '#4F46E5',
        boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)',
        outline: 'none',
      },
      '&::placeholder': {
        color: '#9CA3AF',
      }
    },
    
    // Labels des champs
    formFieldLabel: {
      color: '#111827',
      fontWeight: '500',
      fontSize: '0.875rem',
      marginBottom: '0.5rem',
    },
    
    // Liens (Mot de passe oublié, etc.)
    footerActionLink: {
      color: '#4F46E5',
      fontWeight: '600',
      textDecoration: 'none',
      transition: 'color 0.2s ease',
      '&:hover': {
        color: '#4338CA',
        textDecoration: 'underline',
      },
    },
    
    // Séparateur (Ou continuer avec)
    dividerLine: {
      backgroundColor: '#E5E7EB',
      height: '1px',
    },
    dividerText: {
      color: '#6B7280',
      fontSize: '0.85rem',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    
    // Messages d'erreur
    formFieldErrorText: {
      color: '#EF4444',
      fontSize: '0.875rem',
      marginTop: '0.25rem',
    },
    alertText: {
      color: '#991B1B',
      fontSize: '0.875rem',
    },
    
    // UserButton (Avatar et menu)
    userButtonAvatarBox: {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '50%',
      border: '2px solid #E5E7EB',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: '#4F46E5',
        transform: 'scale(1.05)',
        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
      },
    },
    userButtonPopoverCard: {
      borderRadius: '0.75rem',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
      border: '1px solid #E5E7EB',
    },
    userButtonPopoverActionButton: {
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: '#F3F4F6',
      },
    },
    userButtonPopoverActionButtonText: {
      fontSize: '0.9rem',
      fontWeight: '500',
    },
    
    // Footer
    footerAction: {
      marginTop: '1.5rem',
    },
    footerActionText: {
      color: '#6B7280',
      fontSize: '0.9rem',
    },
    
    // Code de vérification
    formFieldInputShowPasswordButton: {
      color: '#6B7280',
      '&:hover': {
        color: '#111827',
      }
    },
    
    // Badge (ex: Email vérifié)
    badge: {
      backgroundColor: '#10B981',
      color: '#FFFFFF',
      fontSize: '0.75rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.375rem',
      fontWeight: '600',
    },
  },
};

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ClerkProvider 
      publishableKey={clerkPubKey}
      appearance={clerkAppearance}
      localization={frFR}
      signInFallbackRedirectUrl="/"  // Pour les redirections normales
      signUpFallbackRedirectUrl="/"
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>
);